import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { referralPartnerApi } from '@/api/referralPartner';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

type PendingApprovals = {
  partners: Array<{
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    status: string;
    createdAt: string;
  }>;
  rateRequests: Array<{
    id: string;
    partnerId: string;
    hospitalId: string | null;
    milestone: 'NEW' | 'OPD_VISITED' | 'IPD_ADMITTED';
    points: number;
    pendingPoints: number | null;
    approvalStatus: 'PENDING' | 'APPROVED' | 'REJECTED';
    updatedAt: string;
    partner: { id: string; firstName: string; lastName: string; email: string };
    hospital: { id: string; name: string } | null;
    requestedByHospital: { id: string; name: string } | null;
    requestedByStaff: { id: string; name: string } | null;
  }>;
  leadPointRequests: Array<{
    id: string;
    partnerId: string;
    hospitalId: string;
    leadId: string;
    milestone: 'NEW' | 'OPD_VISITED' | 'IPD_ADMITTED';
    requestedPoints: number;
    status: 'PENDING' | 'APPROVED' | 'REJECTED';
    reason?: string | null;
    createdAt: string;
    partner: { id: string; firstName: string; lastName: string; email: string };
    hospital: { id: string; name: string };
    lead: { id: string; patientName: string; phone: string };
    requestedByStaff: { id: string; name: string } | null;
  }>;
};

export default function ReferralPartnerApprovalsPage() {
  const qc = useQueryClient();
  const [rateRejectReasonById, setRateRejectReasonById] = useState<Record<string, string>>({});
  const [leadRejectReasonById, setLeadRejectReasonById] = useState<Record<string, string>>({});
  const [reportHospitalId, setReportHospitalId] = useState<string>('');
  const [reportFrom, setReportFrom] = useState<string>('');
  const [reportTo, setReportTo] = useState<string>('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['partner-approvals'],
    queryFn: async () => {
      const res = await referralPartnerApi.admin.super.pendingApprovals();
      return (res.data?.data ?? { partners: [], rateRequests: [], leadPointRequests: [] }) as PendingApprovals;
    },
  });

  const { data: hospitals = [] } = useQuery({
    queryKey: ['partner-report-hospitals'],
    queryFn: async () => {
      const res = await api.get('/api/super-admin/hospital/fetch-all');
      return res.data?.data ?? [];
    },
  });

  const approvePartnerMut = useMutation({
    mutationFn: (id: string) => referralPartnerApi.admin.super.approvePartner(id),
    onSuccess: () => {
      toast.success('Partner approved');
      qc.invalidateQueries({ queryKey: ['partner-approvals'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to approve partner'),
  });

  const approveRateMut = useMutation({
    mutationFn: (id: string) => referralPartnerApi.admin.super.approveRate(id),
    onSuccess: () => {
      toast.success('Rate approved');
      qc.invalidateQueries({ queryKey: ['partner-approvals'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to approve rate'),
  });

  const rejectRateMut = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      referralPartnerApi.admin.super.rejectRate(id, reason),
    onSuccess: () => {
      toast.success('Rate rejected');
      qc.invalidateQueries({ queryKey: ['partner-approvals'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to reject rate'),
  });

  const approveLeadPointsMut = useMutation({
    mutationFn: (id: string) => referralPartnerApi.admin.super.approveLeadPoints(id),
    onSuccess: () => {
      toast.success('Lead points approved');
      qc.invalidateQueries({ queryKey: ['partner-approvals'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to approve lead points'),
  });

  const rejectLeadPointsMut = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      referralPartnerApi.admin.super.rejectLeadPoints(id, reason),
    onSuccess: () => {
      toast.success('Lead points rejected');
      qc.invalidateQueries({ queryKey: ['partner-approvals'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to reject lead points'),
  });

  const exportReportMut = useMutation({
    mutationFn: async () => {
      if (!reportHospitalId) throw new Error('Select hospital');
      const res = await referralPartnerApi.admin.super.exportHospitalReport(
        reportHospitalId,
        reportFrom || undefined,
        reportTo || undefined
      );
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `partner_report_${reportHospitalId}_${reportFrom || 'from'}_to_${reportTo || 'to'}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
    onSuccess: () => toast.success('Report downloaded'),
    onError: (err: any) => toast.error(err?.message || err?.response?.data?.message || 'Report export failed'),
  });

  const exportDetailedCsvMut = useMutation({
    mutationFn: async () => {
      if (!reportHospitalId) throw new Error('Select hospital');
      const res = await referralPartnerApi.admin.super.exportHospitalDetailedCsv(
        reportHospitalId,
        reportFrom || undefined,
        reportTo || undefined
      );
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `partner_detailed_${reportHospitalId}_${reportFrom || 'from'}_to_${reportTo || 'to'}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
    onSuccess: () => toast.success('Detailed CSV downloaded'),
    onError: (err: any) => toast.error(err?.message || err?.response?.data?.message || 'Detailed CSV export failed'),
  });

  const partners = data?.partners ?? [];
  const rateRequests = data?.rateRequests ?? [];
  const leadPointRequests = data?.leadPointRequests ?? [];

  const summary = useMemo(
    () => ({
      partners: partners.length,
      rates: rateRequests.length,
      leads: leadPointRequests.length,
    }),
    [partners.length, rateRequests.length, leadPointRequests.length]
  );

  return (
    <div className="p-6 space-y-8">
      <div>
        <h1 className="font-artis-serif text-2xl font-semibold text-artis-brown-dark">Partner approvals</h1>
        <p className="text-gray-500">
          Pending: {summary.partners} partners, {summary.rates} rate changes, {summary.leads} lead-wise point requests
        </p>
      </div>

      <section className="rounded-lg border bg-white p-4 space-y-3">
        <h2 className="text-lg font-medium">Download partner report</h2>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-4 sm:items-end">
          <div className="sm:col-span-2">
            <label className="text-xs text-gray-500">Hospital</label>
            <select
              className="mt-1 h-10 w-full rounded-md border bg-background px-3 text-sm"
              value={reportHospitalId}
              onChange={(e) => setReportHospitalId(e.target.value)}
            >
              <option value="">Select…</option>
              {(hospitals as Array<{ id: string; name: string }>).map((h) => (
                <option key={h.id} value={h.id}>
                  {h.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500">From</label>
            <Input type="date" value={reportFrom} onChange={(e) => setReportFrom(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-gray-500">To</label>
            <Input type="date" value={reportTo} onChange={(e) => setReportTo(e.target.value)} />
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            variant="outline"
            onClick={() => exportReportMut.mutate()}
            disabled={exportReportMut.isPending}
          >
            {exportReportMut.isPending ? 'Preparing…' : 'Download ZIP'}
          </Button>
          <Button
            variant="outline"
            onClick={() => exportDetailedCsvMut.mutate()}
            disabled={exportDetailedCsvMut.isPending}
          >
            {exportDetailedCsvMut.isPending ? 'Preparing…' : 'Download detailed CSV'}
          </Button>
        </div>
        <p className="text-xs text-gray-500">
          ZIP includes partners summary + leads + points for the selected hospital and time range.
        </p>
      </section>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {(error as any)?.message || 'Failed to load'}
        </div>
      )}

      {isLoading ? (
        <p className="text-gray-500">Loading…</p>
      ) : (
        <>
          <section className="space-y-3">
            <h2 className="text-lg font-medium">Partner account approvals</h2>
            <div className="rounded-lg border overflow-hidden bg-white">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="px-4 py-2">Name</th>
                    <th className="px-4 py-2">Email</th>
                    <th className="px-4 py-2">Phone</th>
                    <th className="px-4 py-2">Requested</th>
                    <th className="px-4 py-2">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {partners.map((p) => (
                    <tr key={p.id}>
                      <td className="px-4 py-2">{p.firstName} {p.lastName}</td>
                      <td className="px-4 py-2">{p.email}</td>
                      <td className="px-4 py-2">{p.phone}</td>
                      <td className="px-4 py-2">{new Date(p.createdAt).toLocaleString()}</td>
                      <td className="px-4 py-2">
                        <Button size="sm" onClick={() => approvePartnerMut.mutate(p.id)} disabled={approvePartnerMut.isPending}>
                          Approve
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {partners.length === 0 && (
                    <tr>
                      <td className="px-4 py-6 text-center text-gray-500" colSpan={5}>
                        No pending partner approvals
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-medium">Pending milestone rate changes</h2>
            <div className="rounded-lg border overflow-hidden bg-white">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="px-4 py-2">Partner</th>
                    <th className="px-4 py-2">Hospital</th>
                    <th className="px-4 py-2">Milestone</th>
                    <th className="px-4 py-2">Current</th>
                    <th className="px-4 py-2">Requested</th>
                    <th className="px-4 py-2">Requested by</th>
                    <th className="px-4 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rateRequests.map((r) => (
                    <tr key={r.id}>
                      <td className="px-4 py-2">{r.partner.firstName} {r.partner.lastName}</td>
                      <td className="px-4 py-2">{r.hospital?.name || '—'}</td>
                      <td className="px-4 py-2">{r.milestone}</td>
                      <td className="px-4 py-2">{r.points}</td>
                      <td className="px-4 py-2">{r.pendingPoints ?? '—'}</td>
                      <td className="px-4 py-2">{r.requestedByStaff?.name || '—'}</td>
                      <td className="px-4 py-2 space-y-2">
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => approveRateMut.mutate(r.id)} disabled={approveRateMut.isPending}>
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() =>
                              rejectRateMut.mutate({ id: r.id, reason: rateRejectReasonById[r.id] })
                            }
                            disabled={rejectRateMut.isPending}
                          >
                            Reject
                          </Button>
                        </div>
                        <Input
                          placeholder="Rejection reason (optional)"
                          value={rateRejectReasonById[r.id] ?? ''}
                          onChange={(e) =>
                            setRateRejectReasonById((prev) => ({ ...prev, [r.id]: e.target.value }))
                          }
                          className="h-8"
                        />
                      </td>
                    </tr>
                  ))}
                  {rateRequests.length === 0 && (
                    <tr>
                      <td className="px-4 py-6 text-center text-gray-500" colSpan={7}>
                        No pending rate requests
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-medium">Pending lead-wise points requests</h2>
            <div className="rounded-lg border overflow-hidden bg-white">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="px-4 py-2">Lead</th>
                    <th className="px-4 py-2">Hospital</th>
                    <th className="px-4 py-2">Milestone</th>
                    <th className="px-4 py-2">Requested points</th>
                    <th className="px-4 py-2">Requested by</th>
                    <th className="px-4 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {leadPointRequests.map((r) => (
                    <tr key={r.id}>
                      <td className="px-4 py-2">
                        <div className="font-medium">{r.lead.patientName}</div>
                        <div className="text-xs text-gray-500">{r.lead.phone} • {r.lead.id}</div>
                      </td>
                      <td className="px-4 py-2">{r.hospital.name}</td>
                      <td className="px-4 py-2">{r.milestone}</td>
                      <td className="px-4 py-2">{r.requestedPoints}</td>
                      <td className="px-4 py-2">{r.requestedByStaff?.name || '—'}</td>
                      <td className="px-4 py-2 space-y-2">
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => approveLeadPointsMut.mutate(r.id)} disabled={approveLeadPointsMut.isPending}>
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() =>
                              rejectLeadPointsMut.mutate({ id: r.id, reason: leadRejectReasonById[r.id] })
                            }
                            disabled={rejectLeadPointsMut.isPending}
                          >
                            Reject
                          </Button>
                        </div>
                        <Input
                          placeholder="Rejection reason (optional)"
                          value={leadRejectReasonById[r.id] ?? ''}
                          onChange={(e) =>
                            setLeadRejectReasonById((prev) => ({ ...prev, [r.id]: e.target.value }))
                          }
                          className="h-8"
                        />
                      </td>
                    </tr>
                  ))}
                  {leadPointRequests.length === 0 && (
                    <tr>
                      <td className="px-4 py-6 text-center text-gray-500" colSpan={6}>
                        No pending lead-wise point requests
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

